import React, { useEffect, useState } from 'react';
import JournalExpList from '../components/exped/JournalExpList';
import { Button, message, Card, Typography, Space, Input, Form, Alert, Descriptions, DatePicker, Tooltip } from 'antd';
import { SyncOutlined, SearchOutlined, UserOutlined, CloudUploadOutlined, CalendarOutlined, MoreOutlined } from '@ant-design/icons';
import { useCreateJournalExpEntries, useVerifyCompte } from '../hooks/expedHooks';
import { requestExportProgress } from '../services/exportProgress';
import ExportProgress from '../components/shared/ExportProgress';
import moment from 'moment';

const { Title } = Typography;

const JournalExpManagementPage = () => {
  const [form] = Form.useForm();
  const [compte, setCompte] = useState('');
  const [clientInfo, setClientInfo] = useState(null);
  const [expedierLoading, setExpedierLoading] = useState(false);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (expedierLoading || !['completed', 'failed'].includes(progress?.stage)) return;
    const timer = setTimeout(() => setProgress(null), 3000);
    return () => clearTimeout(timer);
  }, [expedierLoading, progress?.stage]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDateFilters, setShowDateFilters] = useState(false);
  
  // Use our new hook
  const { 
    verifyCompte, 
    loading: verifyLoading, 
    error: verifyError, 
    exists: compteExists,
    compteInfo,
    resetCompteInfo
  } = useVerifyCompte();
  
  const { createJournalExpEntries, loading: majLoading } = useCreateJournalExpEntries();

  const handleClear = () => {
    setCompte('');
    setClientInfo(null);
    resetCompteInfo();
    form.resetFields();
  };

  const handleSearch = async () => {
    try {
      const values = await form.validateFields();
      // Convert to uppercase when searching
      const uppercaseCompte = values.compte.trim().toUpperCase();
      
      // Use our new verify compte function
      const result = await verifyCompte(uppercaseCompte);
      
      if (result.exists) {
        setCompte(uppercaseCompte);
        setClientInfo(result.compteInfo);
      }
    } catch (error) {
      // Form validation error
    }
  };

  const handleMajClick = async () => {
    try {
      const result = await createJournalExpEntries();
      message.success(`Journal entries updated successfully. Created: ${result.stats.total} entries`);
    } catch (error) {
      message.error('Failed to update journal entries');
    }
  };

  const handleExpedier = async () => {
    if (expedierLoading) return;
    setExpedierLoading(true);
    setProgress({ stage: 'starting', table: 'Expédition', percent: 0 });
    try {
      const dateParams = {};
      if (startDate) dateParams.startDate = startDate.format('YYYY-MM-DD');
      if (endDate) dateParams.endDate = endDate.format('YYYY-MM-DD');
      
      const result = await requestExportProgress(
        `${process.env.REACT_APP_API_BASE_URL}/exped/export/dbf`, 
        { ...dateParams, progress: true }, setProgress
      );
      
      setProgress(result);
      if (result.success) {
        message.success('Export des journaux d\'expédition complété avec succès');
      } else {
        message.error('Échec de l\'export: ' + result.message);
      }
    } catch (error) {
      setProgress(previous => ({ ...previous, stage: 'failed', message: error.message }));
      console.error('Erreur d\'export:', error);
      message.error('Échec de l\'export: ' + (error.response?.data?.message || error.message));
    } finally {
      setExpedierLoading(false);
    }
  };

  const titleStyle = {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #ccc",
    paddingBottom: "10px",
  };

  // Transform input to uppercase as the user types
  const handleInputChange = (e) => {
    const uppercaseValue = e.target.value.toUpperCase();
    form.setFieldsValue({ compte: uppercaseValue });
  };

  return (
    <div>
      <ExportProgress progress={progress} title="Expédition" />
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Title level={2} style={titleStyle}>
            Expedition {compteExists && clientInfo ? 
              `- ${clientInfo.CLIENT} (${clientInfo.COMPTE}) - ${clientInfo.VILLE || ''}` : ''}
          </Title>
          
          <Space>
            {compteExists && (
              <Button type="default" onClick={handleClear}>
                SORTIR
              </Button>
            )}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={showDateFilters ? "Masquer les filtres de date" : "Afficher les filtres de date"}>
                <Button 
                  type="text" 
                  icon={<MoreOutlined />} 
                  onClick={() => setShowDateFilters(!showDateFilters)}
                  style={{ marginRight: '8px' }}
                />
              </Tooltip>
              <div 
                style={{ 
                  display: 'flex', 
                  maxWidth: showDateFilters ? '300px' : '0',
                  overflow: 'hidden',
                  transition: 'max-width 0.3s ease-in-out',
                  marginRight: showDateFilters ? '8px' : '0'
                }}
              >
                <DatePicker
                  placeholder="Date début"
                  onChange={(date) => setStartDate(date)}
                  value={startDate}
                  style={{ 
                    width: '130px',
                    marginRight: '4px'
                  }}
                />
                <DatePicker
                  placeholder="Date fin"
                  onChange={(date) => setEndDate(date)}
                  value={endDate}
                  style={{ width: '130px' }}
                />
              </div>
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                loading={expedierLoading}
                onClick={handleExpedier}
              >
                EXPEDIER
              </Button>
            </div>
            <Button
              type="primary"
              icon={<SyncOutlined />}
              loading={majLoading}
              onClick={handleMajClick}
            >
              MAJ
            </Button>
          </Space>
        </div>

        {!compteExists && (
          <div style={{ maxWidth: '500px', margin: '0 auto', padding: '20px' }}>
            <Form form={form} layout="vertical">
              <Form.Item 
                name="compte" 
                label="Numéro de Compte"
                rules={[{ required: true, message: 'Veuillez entrer un numéro de compte' }]}
              >
                <Input 
                  placeholder="Entrez le numéro de compte" 
                  size="large"
                  prefix={<SearchOutlined />}
                  onPressEnter={handleSearch}
                  onChange={handleInputChange}
                  style={{ textTransform: 'uppercase' }}
                />
              </Form.Item>
              
              {verifyError && (
                <Alert
                  message="Erreur"
                  description={verifyError}
                  type="error"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}
              
              <Form.Item>
                <Button 
                  type="primary" 
                  onClick={handleSearch} 
                  loading={verifyLoading}
                  icon={<SearchOutlined />}
                  size="large"
                  block
                >
                  Rechercher
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}
        
        {compteExists && clientInfo && (
          <>
          
            
            <JournalExpList 
              compte={compte} 
              clientName={clientInfo.nom || `Compte: ${compte}`}
            />
          </>
        )}
      </Card>
    </div>
  );
};

export default JournalExpManagementPage;